import { KeyboardEvent, useMemo, useState } from "react";
import { PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BaseInput,
  Checkbox,
  CheckboxSize,
  InputSize,
  Show
} from "shared/components";
import { cx } from "shared/utils";
import {
  LEAD_TAG_NAME_MAX_LENGTH,
  LEAD_TAGS_PER_LEAD_LIMIT,
  LeadTag,
  isSameLeadTagName,
  normalizeLeadTagName
} from "api/lead-tags";
import { LeadTagPill } from "shared/app/LeadTags/components/LeadTagPill";
import { useLeadTags } from "shared/app/LeadTags/hooks/useLeadTags";
import { useLeadTagChange } from "shared/app/LeadTags/hooks/useLeadTagChange";

const NOOP = () => {};
// Show the remaining length once the name gets close to the limit.
const SHOW_LENGTH_FROM = 24;

const filterByQuery = (tags: LeadTag[], query: string) => {
  const needle = query.toLowerCase();
  return needle
    ? tags.filter((tag) => tag.name.toLowerCase().includes(needle))
    : tags;
};

const EmptyMessage = ({ title, text }: { title: string; text: string }) => (
  <div className="px-3 py-5 text-center space-y-1">
    <p className="text-sm font-bold text-content-1000">{title}</p>
    <p className="text-sm text-content-400">{text}</p>
  </div>
);

const getEmptyMessage = ({
  hasLibrary,
  hasQuery,
  hasMatches,
  canCreate
}: {
  hasLibrary: boolean;
  hasQuery: boolean;
  hasMatches: boolean;
  canCreate: boolean;
}): { title: string; text: string } | null => {
  if (!hasLibrary && !hasQuery)
    return canCreate
      ? {
          title: "No tags yet",
          text: "Type a name above and press Enter to create your first tag."
        }
      : {
          title: "No tags yet",
          text: "Only admins can create tags. Ask an admin to add some."
        };
  if (hasQuery && !hasMatches && !canCreate)
    return {
      title: "No matching tags",
      text: "Only admins can create new tags."
    };
  return null;
};

/**
 * Picker body: the Lead's tags as removable chips, a search box and every tag A to Z
 * with a checkbox. Admins also get a Create row for a name that matches no tag. Every
 * change saves immediately.
 */
export const LeadTagPickerContent = ({
  leadId,
  tags,
  canCreate
}: {
  leadId: number;
  tags: LeadTag[];
  canCreate: boolean;
}) => {
  const [query, setQuery] = useState("");
  // BaseInput shows its own draft while focused, so clearing the box after Enter
  // remounts it (autoFocus puts the caret back).
  const [inputKey, setInputKey] = useState(0);
  const clearQuery = () => {
    setQuery("");
    setInputKey((key) => key + 1);
  };
  const { data: library = [], isLoading } = useLeadTags();
  const { mutate } = useLeadTagChange({ leadId });

  const normalizedQuery = normalizeLeadTagName(query);
  const selectedIds = useMemo(() => new Set(tags.map((tag) => tag.id)), [tags]);
  const matches = useMemo(
    () => filterByQuery(library, normalizedQuery),
    [library, normalizedQuery]
  );
  const exactMatch = useMemo(
    () =>
      normalizedQuery
        ? library.find((tag) => isSameLeadTagName(tag.name, normalizedQuery))
        : undefined,
    [library, normalizedQuery]
  );

  const isAtLimit = tags.length >= LEAD_TAGS_PER_LEAD_LIMIT;
  const showCreate =
    canCreate && Boolean(normalizedQuery) && !exactMatch && !isLoading;
  const emptyMessage = isLoading
    ? null
    : getEmptyMessage({
        hasLibrary: library.length > 0,
        hasQuery: Boolean(normalizedQuery),
        hasMatches: matches.length > 0,
        canCreate
      });

  const addTag = (tag: LeadTag) => {
    if (selectedIds.has(tag.id) || isAtLimit) return;
    mutate({ kind: "add", tag });
  };

  const toggleTag = (tag: LeadTag) => {
    if (selectedIds.has(tag.id)) mutate({ kind: "remove", tag });
    else addTag(tag);
  };

  const createTag = () => {
    if (!normalizedQuery || isAtLimit) return;
    mutate({ kind: "create", name: normalizedQuery });
    clearQuery();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (exactMatch) {
        addTag(exactMatch);
        clearQuery();
      } else if (canCreate) createTag();
      return;
    }
    if (event.key === "Backspace" && !query && tags.length > 0)
      mutate({ kind: "remove", tag: tags[tags.length - 1] });
  };

  return (
    // The picker renders in a portal, but React still bubbles its clicks to the
    // card / row it was opened from, which would open the Lead.
    <div onClick={(event) => event.stopPropagation()}>
      <div className="px-3 pt-3 pb-2 border-b border-alpha-100 space-y-2">
        <Show if={tags.length > 0}>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <LeadTagPill
                key={tag.id}
                name={tag.name}
                className="max-w-[220px]"
                onRemove={() => mutate({ kind: "remove", tag })}
              />
            ))}
          </div>
        </Show>
        <BaseInput
          key={inputKey}
          id={`lead-${leadId}-tag-search`}
          size={InputSize.SM}
          hideOptional
          autoFocus
          autoComplete="off"
          maxLength={LEAD_TAG_NAME_MAX_LENGTH}
          placeholder={
            canCreate ? "Type to add or create a tag" : "Search tags"
          }
          value={query}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="max-h-[250px] overflow-y-auto py-1">
        <Show if={showCreate}>
          <button
            type="button"
            disabled={isAtLimit}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-bold text-primary-500 hover:bg-alpha-40 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={createTag}
          >
            <HugeiconsIcon icon={PlusSignIcon} size={16} />
            <span className="truncate">Create “{normalizedQuery}”</span>
          </button>
        </Show>
        <Show if={isLoading}>
          <p className="px-4 py-2.5 text-sm text-content-400">
            Loading tags...
          </p>
        </Show>
        {emptyMessage ? <EmptyMessage {...emptyMessage} /> : null}
        <Show if={matches.length > 0}>
          <p className="px-4 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wide text-content-300">
            {normalizedQuery ? "Matching tags" : "All tags"} · A–Z
          </p>
        </Show>
        {matches.map((tag) => {
          const isSelected = selectedIds.has(tag.id);
          const isDisabled = !isSelected && isAtLimit;
          return (
            <div
              key={tag.id}
              role="option"
              aria-selected={isSelected}
              aria-disabled={isDisabled}
              className={cx(
                "flex w-full items-center gap-3 px-4 py-2 text-sm font-medium text-content-1000",
                isDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer hover:bg-alpha-40"
              )}
              onClick={() => {
                if (!isDisabled) toggleTag(tag);
              }}
            >
              <Checkbox
                checked={isSelected}
                disabled={isDisabled}
                size={CheckboxSize.SM}
                wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                onChange={NOOP}
              />
              <span className="truncate">{tag.name}</span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-alpha-40 bg-alpha-20 px-3 py-2 text-xs text-content-400">
        <span>
          {isAtLimit
            ? `A lead can have up to ${LEAD_TAGS_PER_LEAD_LIMIT} tags.`
            : "Changes save automatically"}
        </span>
        <Show if={query.length >= SHOW_LENGTH_FROM}>
          <span>
            {query.length}/{LEAD_TAG_NAME_MAX_LENGTH}
          </span>
        </Show>
      </div>
    </div>
  );
};
